<template>
  <div v-if="!loading" class="q-gutter-y-md parent-card">
    <q-dialog
      v-if="unitMixCsvImportDialog"
      v-model="unitMixCsvImportDialog"
      transition-duration="100"
    >
      <unit-mix-csv-import @finished="unitMixCsvImportFinished" />
    </q-dialog>

    <q-card bordered class="full-width" flat>
      <q-card-section class="row justify-between">
        <div class="q-gutter-x-sm">
          <div class="text-h6">Enter New Development Information</div>
          <div v-if="canCreatePropertyReports" class="text-caption">
            Trying to analyze an existing property?
            <router-link :to="{ name: 'New Property Report' }" class="text-primary">
              Click here
            </router-link>
          </div>
        </div>
      </q-card-section>
      <q-banner v-if="existingPropertyId" class="bg-amber-1 text-black q-mx-md q-mb-md" rounded>
        <template #avatar>
          <q-icon color="amber-9" name="warning" />
        </template>
        <strong>Warning:</strong> Editing this custom development will change the development data
        for <strong>ALL</strong> reports that use this development. If you've duplicated this
        report, those duplicates will also be affected.
      </q-banner>
      <q-separator />
      <q-card-section>
        <address-google-autocomplete
          ref="autocompleteRef"
          :filled="false"
          :rules="[
            (val: string) => {
              if (!location.street_address || !location.city || !location.state || !location.zip) {
                return 'Please select a different address - the selected address is missing required information'
              }

              return true
            }
          ]"
          :value="location.address"
          lazy-rules="ondemand"
          outlined
          @click="selectedAddress"
        />
        <div class="row q-mt-md q-gutter-x-md">
          <q-input v-model="buildingName" class="col" label="Property Name" outlined />
          <q-input
            ref="numberUnitsInput"
            v-model.number="numberUnits"
            :rules="[
              (val) => !!val || 'Number of Units is required',
              (val) => isInteger(val) || 'Number of Units must be an integer',
              (val) => val > 0 || 'Number of Units must be greater than 0',
              (val) => val < 20000 || 'Number of Units must be less than 20000'
            ]"
            class="col"
            label="Number of Units*"
            lazy-rules
            no-error-icon
            outlined
            @blur="getMarketStats"
          />
          <q-input
            ref="yearBuiltInput"
            v-model.number="yearBuilt"
            :rules="[
              (val) => !!val || 'Year Built is required',
              (val) => isInteger(val) || 'Year Built must be an integer',
              (val) => val > 1800 || 'Year Built must be greater than 1800',
              (val) => val < 2030 || 'Year Built must be less than 2030'
            ]"
            class="col"
            label="Year Built*"
            lazy-rules
            no-error-icon
            outlined
            @blur="getMarketStats"
          />
          <q-input
            ref="numberStoriesInput"
            v-model.number="numberStories"
            :rules="[
              (val) => !!val || 'Number of Stories is required',
              (val) => isInteger(val) || 'Number of Stories must be an integer',
              (val) => val > 0 || 'Number of Stories must be greater than 0',
              (val) => val < 100 || 'Number of Stories must be less than 100'
            ]"
            class="col"
            label="Number of Stories*"
            lazy-rules
            no-error-icon
            outlined
            @blur="getMarketStats"
          />
        </div>
      </q-card-section>
    </q-card>

    <q-card bordered class="full-width" flat>
      <q-card-section class="row justify-between">
        <div class="q-gutter-x-sm">
          <div class="text-h6">Enter Unit Mix</div>
        </div>
        <q-btn
          :outline="$q.screen.gt.xs"
          class="q-mr-sm"
          no-caps
          @click="unitMixCsvImportDialog = true"
        >
          CSV Import
        </q-btn>
      </q-card-section>
      <q-separator />
      <q-card-section>
        <q-card :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'" bordered class="q-mb-lg" flat>
          <div class="flex justify-between q-pa-md">
            <div>
              Recommended Unit Mix
              <div class="text-caption">
                Use similar properties in location, type, size and quality to recommend a unit mix.
                <template v-if="marketStats?.count_units && marketStats?.count_properties">
                  Based on {{ marketStats?.count_properties }} buildings and
                  {{ marketStats?.count_units }} units.
                </template>
              </div>
            </div>
            <q-btn
              v-if="recommendedUnitMixes !== null"
              color="primary"
              dense
              flat
              icon="bolt"
              label="Use Recommended"
              no-caps
              @click="useRecommendedUnitMix"
            />
          </div>
          <div v-if="loadingMarketStats" class="q-pa-md row justify-center">
            <div class="text-center">
              <q-spinner-grid color="primary" size="2em" />
              <div class="q-mt-sm">
                Finding similar buildings in type, size, quality among the market ...
              </div>
            </div>
          </div>
          <q-markup-table
            v-else-if="recommendedUnitMixes !== null"
            :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
            dense
            flat
          >
            <thead>
              <tr>
                <th class="text-left" scope="col">Recommended Unit Mix</th>
                <th class="text-right" scope="col">Avg Days on Market</th>
                <th class="text-right" scope="col">Units</th>
                <th class="text-right" scope="col">SF</th>
                <th class="text-right" scope="col">Rent</th>
                <th class="text-right" scope="col">Rent PSF</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="recommendedUnitMixes.length === 0">
                <tr>
                  <td class="text-center" colspan="5">No more recommendations</td>
                </tr>
              </template>
              <tr v-for="um in recommendedUnitMixes" v-else :key="um.name">
                <td class="text-left">{{ um.name }}</td>
                <td class="text-right">{{ formatNumber(um.avg_days_on_market) }}</td>
                <td class="text-right">
                  {{ formatNumber(um.units) }}
                  <span v-if="numberUnits">({{ formatPercent(um.units / numberUnits) }})</span>
                </td>
                <td class="text-right">{{ formatNumber(um.avg_sqft) }}</td>
                <td class="text-right">{{ formatMoney(um.avg_rent) }}</td>
                <td class="text-right">
                  <span v-if="um.avg_rent && um.avg_sqft">
                    {{ formatMoney(um.avg_rent / um.avg_sqft, 2) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </q-markup-table>
        </q-card>
        <q-card bordered flat>
          <div
            :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-1'"
            class="q-pa-md flex justify-between"
          >
            <div class="text-h6">Floorplans</div>
            <div class="q-gutter-x-sm row">
              <q-btn
                :class="{ 'width-150': $q.screen.gt.xs }"
                :flat="!$q.screen.gt.xs"
                :label="$q.screen.gt.xs ? 'Clear All' : ''"
                :outline="$q.screen.gt.xs"
                dense
                icon="delete"
                no-caps
                unelevated
                @click="floorplans = []"
              />
              <q-btn
                :class="{ 'width-150': $q.screen.gt.xs }"
                :flat="!$q.screen.gt.xs"
                :label="$q.screen.gt.xs ? 'New Floorplan' : ''"
                :outline="$q.screen.gt.xs"
                dense
                icon="add"
                no-caps
                unelevated
                @click="newFloorplan"
              />
            </div>
          </div>
          <q-table
            :columns="[
              { name: 'beds', label: '# Beds', field: 'beds', align: 'left' },
              { name: 'baths', label: '# Baths', field: 'baths', align: 'left' },
              { name: 'name', label: 'Floorplan Name', field: 'name', align: 'left' },
              { name: 'units', label: '# Units', field: 'units', align: 'left' },
              {
                name: 'avg_sqft',
                label: 'Avg SF',
                field: 'avg_sqft',
                align: 'left'
              },
              {
                name: 'avg_rent',
                label: 'Avg Rent',
                field: 'avg_rent',
                align: 'left'
              },
              {
                name: 'actions',
                label: '',
                field: 'actions'
              }
            ]"
            :hide-bottom="!!floorplans.length"
            :rows="floorplans"
            :rows-per-page-options="[0]"
            bordered
            dense
            flat
            row-key="beds"
          >
            <template #no-data>
              <div class="full-width q-my-md text-center">
                <q-btn
                  class="width-150"
                  color="primary"
                  dense
                  icon="add"
                  label="New Floorplan"
                  no-caps
                  outline
                  unelevated
                  @click="newFloorplan"
                />
              </div>
            </template>
            <template #body="props">
              <q-tr :props="props">
                <q-td key="beds" :props="props">
                  <q-input
                    :ref="`beds-${props.rowIndex}`"
                    v-model.number="props.row.beds"
                    :rules="[(val) => parseInt(val) >= 0 && Number.isInteger(val)]"
                    dense
                    hide-bottom-space
                    lazy-rules
                    outlined
                    type="number"
                  />
                </q-td>
                <q-td key="baths" :props="props">
                  <q-input
                    :ref="`baths-${props.rowIndex}`"
                    v-model.number="props.row.baths"
                    :rules="[(val) => parseInt(val) > 0]"
                    dense
                    hide-bottom-space
                    lazy-rules
                    outlined
                    type="number"
                  />
                </q-td>
                <q-td key="name" :props="props">
                  <q-input v-model="props.row.name" dense outlined />
                </q-td>
                <q-td key="units" :props="props">
                  <q-input
                    :ref="`units-${props.rowIndex}`"
                    v-model.number="props.row.units"
                    :rules="[(val) => parseInt(val) > 0 && Number.isInteger(val)]"
                    dense
                    hide-bottom-space
                    lazy-rules
                    outlined
                    type="number"
                  />
                </q-td>
                <q-td key="avg_sqft" :props="props">
                  <q-input
                    :ref="`avg_sqft-${props.rowIndex}`"
                    v-model.number="props.row.avg_sqft"
                    :rules="[(val) => parseInt(val) > 100 && Number.isInteger(val)]"
                    dense
                    hide-bottom-space
                    lazy-rules
                    outlined
                    type="number"
                  />
                </q-td>
                <q-td key="avg_rent" :props="props">
                  <q-input
                    :ref="`avg_rent-${props.rowIndex}`"
                    v-model.number="props.row.avg_rent"
                    :rules="[(val) => !!val && val > 300]"
                    dense
                    hide-bottom-space
                    lazy-rules
                    outlined
                    type="number"
                  />
                </q-td>
                <q-td key="actions" :props="props">
                  <div class="row">
                    <q-btn
                      class="col q-mr-sm"
                      color="primary"
                      dense
                      flat
                      icon="content_copy"
                      round
                      @click="duplicateFloorplan(props.rowIndex)"
                    />
                    <q-btn
                      class="col"
                      color="red"
                      dense
                      flat
                      icon="delete"
                      round
                      @click="deleteFloorplan(props.rowIndex)"
                    />
                  </div>
                </q-td>
              </q-tr>
            </template>
          </q-table>
        </q-card>
      </q-card-section>
    </q-card>

    <q-card bordered class="full-width" flat>
      <q-card-section class="row justify-between">
        <div class="q-gutter-x-sm">
          <div class="text-h6">Select Amenities</div>
          <div class="text-caption">
            Use similar properties in location, type, size and quality to recommend building and
            unit amenities.
          </div>
        </div>
      </q-card-section>
      <q-separator />
      <q-card-section>
        <div class="row q-mt-sm q-gutter-x-md">
          <q-card bordered class="col" flat>
            <q-table
              :columns="amenityColumns"
              :pagination="{ rowsPerPage: 0 }"
              :rows="recommendedBuildingAmenities"
              :rows-per-page-options="[0]"
              bordered
              dense
              row-key="name"
              table-style="max-height: 500px"
              virtual-scroll
            >
              <template #top>
                <div class="flex justify-between full-width">
                  <div class="q-my-auto">Building Amenities</div>
                  <q-btn
                    :disabled="recommendedBuildingAmenities.length === 0"
                    color="primary"
                    dense
                    flat
                    icon="bolt"
                    label="Use Recommended"
                    no-caps
                    @click="useRecommendedBuildingAmenities"
                  />
                </div>
              </template>
              <template #body="props">
                <q-tr :props="props" dense>
                  <q-td key="name" :props="props">
                    {{ formatKey(props.row.name) }}
                  </q-td>
                  <q-td key="frequency" :props="props">
                    {{ formatPercent(props.row.frequency, 0) }}
                  </q-td>
                  <q-td key="actions" :props="props">
                    <q-btn
                      v-if="buildingAmenities.includes(props.row.name)"
                      color="green"
                      flat
                      icon="check"
                      round
                      @click="
                        buildingAmenities = buildingAmenities.filter((_) => _ !== props.row.name)
                      "
                    />
                    <q-btn
                      v-else
                      color="primary"
                      flat
                      icon="add"
                      round
                      @click="buildingAmenities.push(props.row.name)"
                    />
                  </q-td>
                </q-tr>
              </template>
              <template #no-data>
                <div v-if="loadingMarketStats" class="q-pa-md row justify-center">
                  <div class="text-center">
                    <q-spinner-grid color="primary" size="2em" />
                    <div class="q-mt-sm">
                      Finding similar buildings in type, size, quality among the market ...
                    </div>
                  </div>
                </div>
                <div v-else class="full-width q-my-md text-center">
                  We were not able to find any recommended building amenities for this property.
                </div>
              </template>
            </q-table>
          </q-card>
          <q-card bordered class="col" flat>
            <q-table
              :columns="amenityColumns"
              :pagination="{ rowsPerPage: 0 }"
              :rows="recommendedUnitAmenities"
              :rows-per-page-options="[0]"
              bordered
              dense
              row-key="name"
              table-style="max-height: 500px"
              virtual-scroll
            >
              <template #top>
                <div class="flex justify-between full-width">
                  <div class="q-my-auto">Unit Amenities</div>
                  <div>
                    <q-btn
                      :disabled="recommendedUnitAmenities.length === 0"
                      color="primary"
                      dense
                      flat
                      icon="bolt"
                      label="Use Recommended"
                      no-caps
                      @click="useRecommendedUnitAmenities"
                    />
                  </div>
                </div>
              </template>
              <template #body="props">
                <q-tr :props="props">
                  <q-td key="name" :props="props">
                    {{ formatKey(props.row.name) }}
                  </q-td>
                  <q-td key="frequency" :props="props">
                    {{ formatPercent(props.row.frequency, 0) }}
                  </q-td>
                  <q-td key="actions" :props="props">
                    <q-btn
                      v-if="unitAmenities.includes(props.row.name)"
                      color="green"
                      flat
                      icon="check"
                      round
                      @click="unitAmenities = unitAmenities.filter((_) => _ !== props.row.name)"
                    />
                    <q-btn
                      v-else
                      color="primary"
                      flat
                      icon="add"
                      round
                      @click="unitAmenities.push(props.row.name)"
                    />
                  </q-td>
                </q-tr>
              </template>
              <template #no-data>
                <div v-if="loadingMarketStats" class="q-pa-md row justify-center">
                  <div class="text-center">
                    <q-spinner-grid color="primary" size="2em" />
                    <div class="q-mt-sm">
                      Finding similar buildings in type, size, quality among the market ...
                    </div>
                  </div>
                </div>
                <div v-else class="full-width q-my-md text-center">
                  We were not able to find any recommended unit amenities for this property.
                </div>
              </template>
            </q-table>
          </q-card>
        </div>
      </q-card-section>
    </q-card>

    <div class="q-pa-md row justify-center">
      <q-space />
      <q-btn :to="{ name: 'Dashboard' }" class="q-mr-md" flat no-caps unelevated> Cancel </q-btn>
      <q-btn
        :disable="missingField !== null"
        :label="gotoEditNext ? 'Save & Continue' : gotoReportIdNext ? 'Save & View Report' : 'Save'"
        :loading="savingNewProperty"
        color="primary"
        no-caps
        type="submit"
        unelevated
        @click="submit"
      >
        <q-tooltip v-if="missingField" class="text-caption"> Missing {{ missingField }} </q-tooltip>
      </q-btn>
    </div>

    <safe-teleport to="#toolbar-portal">
      <q-toolbar
        :class="{ 'q-px-md': $q.screen.gt.xs, 'q-px-lg': $q.screen.gt.md }"
        :style="`border-bottom: 1px solid ${
          $q.dark.isActive ? 'rgba(255, 255, 255, 0.28)' : 'rgba(0, 0, 0, 0.12)'
        }`"
        class="q-px-sm row justify-center"
        style="height: 75px"
      >
        <div class="parent-card full-width row justify-end q-gutter-x-sm">
          <div style="font-size: 20px; font-weight: 500">New Development</div>
          <q-space />
          <q-btn :to="{ name: 'Dashboard' }" class="q-mr-md" flat no-caps unelevated>
            Cancel
          </q-btn>
          <q-btn
            :disable="missingField !== null"
            :label="
              gotoEditNext ? 'Save & Continue' : gotoReportIdNext ? 'Save & View Report' : 'Save'
            "
            :loading="savingNewProperty"
            color="primary"
            no-caps
            type="submit"
            unelevated
            @click="submit"
          >
            <q-tooltip v-if="missingField" class="text-caption">
              Missing {{ missingField }}
            </q-tooltip>
          </q-btn>
        </div>
      </q-toolbar>
    </safe-teleport>
  </div>
  <div v-else class="q-pa-md row justify-center">
    <q-inner-loading :visible="loading">
      <q-spinner-gears color="primary" size="2em" />
      <div class="q-mt-sm">Loading property information ...</div>
    </q-inner-loading>
  </div>
</template>

<script lang="ts">
import { QInput, type QTableColumn } from 'quasar'

import {
  type BuildingAmenity,
  type MarketStatsResponse,
  type PropertyReportUnitInsert,
  type UnitAmenity,
  estimateUnitMix
} from 'common'
import propertyApi from '@/api/property'
import { usePropertyStore } from '@/stores/property'
import UnitMixCsvImport from '@/components/UnitMixCsvImport.vue'
import AddressGoogleAutocomplete from '@/components/PropertyReports/AddressGoogleAutocomplete.vue'
import { formatKey, formatMoney, formatNumber, formatPercent } from '@/utils'
import { useUserStore } from '@/stores/user.ts'

type Floorplan = {
  beds: number
  baths: number
  name: string
  units: number
  avg_sqft: number | string | null
  avg_rent: number | string | null
  avg_days_on_market: number | null
}

const isInteger = (val: number | null): boolean => {
  if (val === null) {
    return true
  }
  return Number.isInteger(val)
}

const cleanNumber = (val: number | string | null): number | null => {
  switch (typeof val) {
    case 'number':
      return val
    case 'string':
      if (val === '') {
        return null
      }
      return parseInt(val)
    default:
      return null
  }
}

export default {
  name: 'CardEditProperty',
  components: { AddressGoogleAutocomplete, UnitMixCsvImport },
  props: {
    gotoEditNext: {
      type: Boolean,
      description:
        'If set to true, the user will be redirected to finish editing a report after creating a new property',
      default: false
    },
    existingPropertyId: {
      type: String as () => string | null,
      description: 'The ID of the existing property to edit',
      default: null
    },
    gotoReportIdNext: {
      type: Number as () => number | null,
      description:
        'If set to a value, the user will be redirected to view that report after updating the custom property',
      default: null
    }
  },
  data() {
    return {
      isInteger,
      formatMoney,
      formatNumber,
      formatPercent,
      loading: false,
      assetType: 'Multifamily',
      savingNewProperty: false,
      location: {
        address: '',
        street_address: '',
        city: '',
        state: '',
        zip: '',
        lat: null as number | null,
        lon: null as number | null
      },
      buildingName: null as string | null,
      yearBuilt: null as number | null,
      numberUnits: null as number | null,
      numberStories: null as number | null,
      avgQuality: 0.85 as number | null,
      floorplans: [] as Floorplan[],
      marketStats: null as MarketStatsResponse | null,
      buildingAmenities: [] as BuildingAmenity[],
      unitAmenities: [] as UnitAmenity[],
      loadingMarketStats: false,
      amenityColumns: [
        { name: 'name', label: 'Amenity', field: (_) => formatKey(_.name), align: 'left' },
        {
          name: 'frequency',
          label: 'Market Frequency',
          field: (_) => formatPercent(_.frequency, 0),
          align: 'right'
        },
        { name: 'actions', label: 'Action', field: 'actions' }
      ] as QTableColumn[],
      unitMixCsvImportDialog: false
    }
  },
  computed: {
    missingField(): string | null {
      if (this.location.lat === null || this.location.lon === null) {
        return 'Address'
      }

      if (!this.location.zip) {
        return 'Zip Code'
      }

      if (!this.location.street_address) {
        return 'Street Address'
      }

      if (!this.location.city) {
        return 'City'
      }

      if (!this.location.state) {
        return 'State'
      }

      if (this.yearBuilt === null) {
        return 'Year Built'
      }

      if (this.numberUnits === null) {
        return 'Number of Units'
      }

      if (this.numberStories === null) {
        return 'Number of Stories'
      }

      if (this.avgQuality === null) {
        return 'Overall Quality'
      }

      if (this.floorplans.length === 0) {
        return 'Floorplans'
      }

      return null
    },
    recommendedUnitMixes(): Floorplan[] | null {
      // we use the market stats (avg_unit_mix) to offer a list of viable unit mixes
      const subjectNumberUnits = this.numberUnits
      if (this.marketStats === null || subjectNumberUnits === null) {
        return null
      }
      const unitMixes = this.marketStats.avg_unit_mix
      if (unitMixes.length === 0) {
        return null
      }
      return unitMixes
        .filter((_) => this.floorplans.every((f) => f.name !== `Market ${_.beds}BR/${_.baths}BA`))
        .sort((a, b) => a.beds - b.beds || a.baths - b.baths)
        .map((unitMix) => {
          return {
            beds: unitMix.beds,
            baths: unitMix.baths,
            name: `${unitMix.beds}BR/${unitMix.baths}BA`,
            units: Math.max(Math.round(unitMix.frequency * subjectNumberUnits), 1),
            avg_sqft: unitMix.avg_sqft,
            avg_rent: unitMix.avg_rent,
            avg_days_on_market: unitMix.avg_days_on_market ?? null
          }
        })
    },
    recommendedBuildingAmenities(): { name: string; frequency: number }[] {
      if (this.marketStats === null || this.marketStats.building_amenity_frequencies === null) {
        return []
      }
      return Object.entries(this.marketStats.building_amenity_frequencies)
        .filter(([, frequency]) => frequency >= 0.05)
        .sort(([, a], [, b]) => b - a)
        .map(([amenity, frequency]) => ({ name: amenity, frequency }))
    },
    recommendedUnitAmenities(): { name: string; frequency: number }[] {
      if (this.marketStats === null || this.marketStats.unit_amenity_frequencies === null) {
        return []
      }
      return Object.entries(this.marketStats.unit_amenity_frequencies)
        .filter(([, frequency]) => frequency >= 0.05)
        .sort(([, a], [, b]) => b - a)
        .map(([amenity, frequency]) => ({ name: amenity, frequency }))
    },
    canCreatePropertyReports() {
      return useUserStore().canCreatePropertyReports
    }
  },
  async created() {
    if (this.existingPropertyId) {
      this.loading = true
      await usePropertyStore()
        .details(this.existingPropertyId)
        .then((data) => {
          this.location = {
            address: data.street_address,
            street_address: data.street_address,
            city: data.city,
            state: data.state,
            zip: data.zip_code,
            lat: data.lat,
            lon: data.lon
          }
          this.buildingName = data.building_name
          this.yearBuilt = data.year_built
          this.numberUnits = data.number_units
          this.numberStories = data.number_stories
          this.avgQuality = data.building_quality.property_overall_quality
          this.assetType = data.is_single_family
            ? 'Single Family'
            : data.is_apartment
              ? 'Multifamily'
              : 'Condo'

          const roundNumber = (val: number | null): number | null => {
            if (val === null) {
              return null
            }
            return Math.round(val)
          }
          const estimatedUnitMix = estimateUnitMix(data, 'floorplan')
          this.floorplans = estimatedUnitMix.map((unit) => ({
            beds: unit.bed,
            baths: unit.partialBath ? unit.bath + 0.5 : unit.bath,
            name: unit.name || `${unit.bed}BR/${unit.partialBath ? unit.bath + 0.5 : unit.bath}BA`,
            units: unit.estimatedUnits || 0,
            avg_sqft: roundNumber(unit.avgSqft) || null,
            avg_rent: roundNumber(unit.avgEffectiveRent) || null,
            avg_days_on_market: roundNumber(unit.avgDaysOnMarket) || null
          }))
          this.buildingAmenities = data.building_amenities || []
          this.unitAmenities = data.unit_amenities || []
        })
        .finally(() => {
          this.loading = false
          // wait for a tick before getting market stats
          this.$nextTick(() => {
            this.getMarketStats()
          })
        })
    }
  },
  methods: {
    formatKey,
    useRecommendedBuildingAmenities() {
      if (this.marketStats === null || this.marketStats.building_amenity_frequencies === null) {
        return null
      }

      this.buildingAmenities = Object.entries(this.marketStats.building_amenity_frequencies)
        .filter(([, frequency]) => frequency >= 0.2)
        .map(([amenity]) => amenity as BuildingAmenity)

      this.$tracker.usedRecommendedAmenities('building')
    },
    useRecommendedUnitAmenities() {
      if (this.marketStats === null || this.marketStats.unit_amenity_frequencies === null) {
        return null
      }

      this.unitAmenities = Object.entries(this.marketStats.unit_amenity_frequencies)
        .filter(([, frequency]) => frequency >= 0.2)
        .map(([amenity]) => amenity as UnitAmenity)

      this.$tracker.usedRecommendedAmenities('unit')
    },
    useRecommendedUnitMix() {
      if (this.recommendedUnitMixes === null) {
        return
      }
      this.floorplans = this.recommendedUnitMixes
      this.$tracker.usedRecommendedUnitMix()
    },
    async getMarketStats() {
      if (this.location.lat === null || this.location.lon === null) {
        return
      }
      if (
        (this.yearBuilt && !(this.$refs['yearBuiltInput'] as QInput).validate()) ||
        (this.numberUnits && !(this.$refs['numberUnitsInput'] as QInput).validate()) ||
        (this.numberStories && !(this.$refs['numberStoriesInput'] as QInput).validate())
      ) {
        return
      }
      this.loadingMarketStats = true
      this.marketStats = null
      await usePropertyStore()
        .marketStatistics({
          lat: this.location.lat,
          lon: this.location.lon,
          is_single_family: this.assetType === 'Single Family',
          is_apartment: this.assetType === 'Multifamily',
          is_condo: this.assetType === 'Condo',
          year_built: this.yearBuilt || null,
          number_units: this.numberUnits || null,
          number_stories: this.numberStories || null,
          avg_quality_prediction: this.avgQuality
        })
        .then((stats) => {
          this.marketStats = stats
        })
        .finally(() => {
          this.loadingMarketStats = false
        })
    },
    duplicateFloorplan(idx: number) {
      this.floorplans.push({ ...this.floorplans[idx] })
    },
    async selectedAddress(place: google.maps.places.PlaceResult) {
      this.location.lat = place.geometry?.location?.lat() || null
      this.location.lon = place.geometry?.location?.lng() || null
      const geoLookup: {
        [key: string]: { short_name: string; long_name: string }
      } = {}
      place.address_components?.forEach((component) => {
        component.types.forEach((componentType) => {
          geoLookup[componentType] = component
        })
      })
      const getStreetNumber = () => {
        return (geoLookup['street_number'] || {})['long_name']
      }
      const getStreetName = () => {
        return (geoLookup['route'] || {})['long_name']
      }
      const getCity = () => {
        const a = geoLookup['locality'] || {}
        const b = geoLookup['sublocality'] || {}
        const c = geoLookup['sublocality_level_1'] || {}
        const d = geoLookup['neighborhood'] || {}
        return a['long_name'] || b['long_name'] || c['long_name'] || d['long_name']
      }
      const getState = () => {
        return (geoLookup['administrative_area_level_1'] || {})['short_name']
      }
      const getZipCode = () => {
        return (geoLookup['postal_code'] || {})['short_name']
      }
      this.location.address = place.formatted_address || ''
      this.location.street_address = `${getStreetNumber()} ${getStreetName()}`
      this.location.city = getCity()
      this.location.state = getState()
      this.location.zip = getZipCode()

      const autocompleteInputRef = this.$refs['autocompleteRef'] as QInput
      const isAutocompleteValid = autocompleteInputRef.validate()

      // Escape hatch if address doesn't contain all the necessary components
      if (!isAutocompleteValid) {
        return
      }

      await this.getMarketStats()
    },
    deleteFloorplan(idx: number) {
      this.floorplans = this.floorplans.filter((_, i) => i !== idx)
    },
    gotoNextPage(id: string) {
      // TODO: If killing the overview page validated, simplify this logic
      if (this.gotoEditNext) {
        this.$router.push({ name: 'New Property Report', query: { propertyId: id } })
      } else if (this.gotoReportIdNext) {
        this.$router.push({ name: 'Property Report', params: { reportId: this.gotoReportIdNext } })
      } else {
        this.$router.push({ name: 'New Property Report', query: { propertyId: id } })
      }
    },
    newFloorplan() {
      this.floorplans.push({
        beds: 0,
        baths: 1,
        name: '',
        units: 1,
        avg_sqft: null,
        avg_rent: null,
        avg_days_on_market: null
      })
    },
    submit() {
      const validateRefs = [
        'autocompleteRef',
        'yearBuiltInput',
        'numberUnitsInput',
        'numberStoriesInput'
      ]
      this.floorplans.forEach((_, index) => {
        validateRefs.push(`beds-${index}`)
        validateRefs.push(`baths-${index}`)
        validateRefs.push(`units-${index}`)
        validateRefs.push(`avg_sqft-${index}`)
        validateRefs.push(`avg_rent-${index}`)
      })
      const validations = validateRefs
        .map((ref) => (this.$refs[ref] as QInput).validate())
        .filter((_) => !_)
      if (validations.length) {
        return
      }
      if (this.missingField !== null) {
        return
      }
      this.savingNewProperty = true

      if (!this.existingPropertyId) {
        propertyApi
          .insertProperty({
            street_address: this.location.street_address,
            city: this.location.city,
            state: this.location.state,
            zip_code: this.location.zip,
            lat: this.location.lat!,
            lon: this.location.lon!,
            year_built: this.yearBuilt!,
            number_units: this.numberUnits!,
            number_stories: this.numberStories!,
            is_single_family: this.assetType === 'Single Family',
            is_apartment: this.assetType === 'Multifamily',
            is_condo: this.assetType === 'Condo',
            building_amenities: this.buildingAmenities,
            unit_amenities: this.unitAmenities,
            building_name: this.buildingName,
            avg_quality: this.avgQuality!,
            units: this.floorplans.flatMap((floorplan) =>
              new Array(Math.round(floorplan.units)).fill({
                bed: cleanNumber(floorplan.beds),
                bath: floorplan.baths,
                sqft: cleanNumber(floorplan.avg_sqft),
                price: cleanNumber(floorplan.avg_rent),
                name: floorplan.name
              } as PropertyReportUnitInsert)
            )
          })
          .then((newID: { id: string } | null) => {
            if (newID !== null) {
              this.gotoNextPage(newID.id)
            }
          })
          .catch((e) => {
            console.error(e)
            this.$q.notify({
              message:
                'An error occurred while saving the property. We are aware of the issue and are working to resolve it. Please try again later.',
              color: 'negative',
              position: 'top'
            })
          })
          .finally(() => {
            this.savingNewProperty = false
          })
      } else {
        propertyApi
          .updateProperty(this.existingPropertyId, {
            street_address: this.location.street_address,
            city: this.location.city,
            state: this.location.state,
            zip_code: this.location.zip,
            lat: this.location.lat!,
            lon: this.location.lon!,
            year_built: this.yearBuilt!,
            number_units: this.numberUnits!,
            number_stories: this.numberStories!,
            is_single_family: this.assetType === 'Single Family',
            is_apartment: this.assetType === 'Multifamily',
            is_condo: this.assetType === 'Condo',
            building_amenities: this.buildingAmenities,
            unit_amenities: this.unitAmenities,
            building_name: this.buildingName,
            avg_quality: this.avgQuality!,
            units: this.floorplans.flatMap((floorplan) =>
              new Array(Math.round(floorplan.units)).fill({
                bed: floorplan.beds,
                bath: floorplan.baths,
                sqft: cleanNumber(floorplan.avg_sqft),
                price: cleanNumber(floorplan.avg_rent),
                name: floorplan.name
              } as PropertyReportUnitInsert)
            )
          })
          .then(() => {
            this.gotoNextPage(this.existingPropertyId)
          })
          .catch((e: Error) => {
            console.error(e)
            this.$q.notify({
              message:
                'An error occurred while saving the property. We are aware of the issue and are working to resolve it. Please try again later.',
              color: 'negative',
              position: 'top'
            })
          })
          .finally(() => {
            this.savingNewProperty = false
          })
      }
    },
    unitMixCsvImportFinished(floorplans: Floorplan[]) {
      this.unitMixCsvImportDialog = false
      this.floorplans = this.floorplans.concat(floorplans)
      this.$q.notify({
        message: `CSV Upload Completed: ${floorplans.length} units added`,
        color: 'positive',
        position: 'top'
      })
    }
  }
}
</script>
<style scoped>
.parent-card {
  max-width: 1600px;
  margin: auto;
}
.width-150 {
  width: 150px;
}
</style>
